import '@cbhq/cds-fonts/fonts.css';
import '@cbhq/cds-icons/fonts/web/icon-font.css';
import '@cbhq/cds-icons/v7/fonts/web/icon-font.css';
import '@cbhq/cds-web/globalStylesV7.css';
import '@cbhq/cds-web/globalStyles';
import 'focus-visible';
import ':support/styles/global.css';

import { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import NextApp, { AppContext, AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { ClientContextProvider as AppErrorVitalsClientContextProvider } from '@cbhq/error-vitals-web';
import { IdentityProvider } from '@cbhq/identity-helpers';
import {
  convertToCBSupported,
  DEFAULT_LOCALE,
  fetchMessagesOnServer,
  MessagesMap,
} from '@cbhq/intl';
import { polyfillIntl } from '@cbhq/intl/polyfills';

import { AppErrorBoundary } from ':support/components/AppErrorBoundary';
import { GlobalWrapper } from ':support/components/GlobalWrapper';
import { UnauthenticatedApp } from ':support/components/UnauthenticatedApp';
import { DynamicConfig, getStaticConfig } from ':support/config';
import { CDSProvider } from ':support/providers/CDSProvider';
import { DynamicConfigProvider } from ':support/providers/DynamicConfigProvider';
import { QueryProvider } from ':support/providers/QueryProvider';
import { SessionProvider } from ':support/providers/SessionProvider';
import { SpectrumProvider } from ':support/providers/SpectrumProvider';
import { initAPIClient } from ':support/utils/api';
import { initClientAnalytics } from ':support/utils/clientAnalytics';
import { errorVitalsClientContext, initErrorVitals } from ':support/utils/error-vitals/init';

// Define our custom page props
type CustomPageProps = {
  dynamicConfig: DynamicConfig;
  messages?: MessagesMap;
  [key: string]: unknown;
};

// Use the standard Next.js AppProps with our custom props
type CustomAppProps = AppProps<CustomPageProps>;

export function App({ Component, pageProps }: CustomAppProps) {
  const router = useRouter();
  const [dynamicConfig] = useState(pageProps.dynamicConfig);
  const [messages] = useState<MessagesMap | undefined>(pageProps.messages);
  const { locale = DEFAULT_LOCALE } = router;

  // Initialize error vitals on client-side (upgrades from fallback to real client)
  useEffect(() => {
    initErrorVitals();
  }, []);

  // Calling useAPIClient here ensures that the API client is initialized before any API requests are made
  useEffect(() => {
    initAPIClient();
  }, []);

  // Initialize Coinbase Client Analytics
  useEffect(() => {
    initClientAnalytics(router);
  }, [router]);

  // Polyfill on mount
  useEffect(() => {
    void polyfillIntl(convertToCBSupported(locale));
  }, [locale]);

  // TODO: uncomment this block when cx_support_hub is added to the kill switch service
  // useEffect(() => {
  //   initKillSwitch();
  // }, []);

  return (
    <AppErrorVitalsClientContextProvider value={errorVitalsClientContext}>
      <SpectrumProvider>
        <CDSProvider>
          <IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={messages}>
            <IdentityProvider>
              <AppErrorBoundary name="cx_support_app_initialized">
                <DynamicConfigProvider config={dynamicConfig}>
                  <QueryProvider>
                    <SessionProvider locale={locale} fallback={<UnauthenticatedApp />}>
                      <GlobalWrapper locale={locale}>
                        <Component />
                      </GlobalWrapper>
                    </SessionProvider>
                  </QueryProvider>
                </DynamicConfigProvider>
              </AppErrorBoundary>
            </IdentityProvider>
          </IntlProvider>
        </CDSProvider>
      </SpectrumProvider>
    </AppErrorVitalsClientContextProvider>
  );
}

App.getInitialProps = async (context: AppContext): Promise<{ pageProps: CustomPageProps }> => {
  const appProps = await NextApp.getInitialProps(context);

  if (typeof window === 'undefined') {
    // Use dynamic import with webpack externals configuration
    const { fetchDynamicConfig } = await import(':support/config/dynamicConfig');
    const dynamicConfig = await fetchDynamicConfig();

    // Fetch messages on server-side
    const { smartlingProjectId } = getStaticConfig();
    let messages: MessagesMap | undefined;
    if (context.router?.locale && smartlingProjectId) {
      messages = await fetchMessagesOnServer(
        convertToCBSupported(context.router.locale),
        smartlingProjectId,
      );
    }

    appProps.pageProps.dynamicConfig = dynamicConfig;
    appProps.pageProps.messages = messages;
  }

  return appProps;
};

export default App;
